import posthog from 'posthog-js'

const key = import.meta.env.VITE_POSTHOG_KEY as string | undefined
const host = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ?? 'https://us.i.posthog.com'

// ── Init ──────────────────────────────────────────────────────────────────────

export function initAnalytics() {
  if (!key) return
  posthog.init(key, {
    api_host: host,
    person_profiles: 'identified_only', // no anonymous profiles
    capture_pageview: false,            // manual pageviews via capturePageview()
    capture_pageleave: true,
    autocapture: false,                 // explicit events only — no click noise
    capture_exceptions: true,           // error tracking: unhandled errors + promise rejections
  })
}

// ── Identity ──────────────────────────────────────────────────────────────────

export function identifyUser(userId: string, props?: { email?: string }) {
  if (!key) return
  posthog.identify(userId, props)
}

export function resetUser() {
  if (!key) return
  posthog.reset()
}

// ── Core capture ──────────────────────────────────────────────────────────────

export function capture(event: string, props?: Record<string, unknown>) {
  if (!key) return
  posthog.capture(event, props)
}

export function capturePageview(path: string) {
  if (!key) return
  posthog.capture('$pageview', { $current_url: window.location.origin + path })
}

// Errors caught by React error boundaries (capture_exceptions only sees uncaught ones)
export function captureError(error: unknown, context?: Record<string, unknown>) {
  if (!key) return
  posthog.captureException(error, context)
}

// ── Typed event helpers ───────────────────────────────────────────────────────

export const analytics = {
  appOpened(props: { sport?: string; theme: string }) {
    capture('app_opened', props)
  },

  liveGameStarted(props: { sport: string; period_count: number; period_label: string }) {
    capture('live_game_started', props)
  },

  statRecorded(props: { stat_key: string; sport: string; period: string }) {
    capture('stat_recorded', props)
  },

  gameEnded(props: { sport: string; result: string; home_score: number; opponent_score: number; stat_count: number }) {
    capture('game_ended', props)
  },

  lineupSaved(props: { sport: string; player_count: number }) {
    capture('lineup_saved', props)
  },

  shareLinkGenerated(props: { type: 'game' | 'season' | 'availability' }) {
    capture('share_link_generated', props)
  },

  dataExported(props: { type: 'stats' | 'roster' | 'game_log' | 'game_stats' }) {
    capture('data_exported', props)
  },

  teamCreated(props: { sport: string }) {
    capture('team_created', props)
  },

  sportSelected(props: { sport: string }) {
    capture('sport_selected', props)
  },

  appShared() {
    capture('app_shared')
  },
}
